import Finance from 'tvm-financejs'

import { RATES } from './consts'

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined
}

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T // from lodash

export function truthy<T>(value: T): value is Truthy<T> {
  return !!value
}

const finance = new Finance()

export const getRate = (amount: number) => {
  return RATES.filter(
    rate => amount >= rate.lowerRange && amount <= rate.upperRange,
  )
}

export const getCrystallisedInterest = (
  loanAtStartOfTheMonth: number,
  interestRate: number,
) => {
  return (loanAtStartOfTheMonth * (interestRate / 100)) / 12
}

export const getScheduledPrincipal = (
  currentMonth: number,
  loanTermMonths: number,
  remainingMonths: number,
  loanAtStartOfTheMonth: number,
  crystallisedInterest: number,
  interestRate: number,
) => {
  if (currentMonth + 1 === loanTermMonths) {
    return loanAtStartOfTheMonth + crystallisedInterest
  }

  const IR = interestRate / 100

  const result =
    loanAtStartOfTheMonth *
    Math.pow(1 + IR / 12, remainingMonths) *
    (IR / 12 / (Math.pow(1 + IR / 12, remainingMonths) - 1))

  return result
}

export const getLoanAtTheEndOfTheMonth = (
  loanAtStartOfTheMonth: number,
  crystallisedInterest: number,
  scheduledPrincipal: number,
) => {
  return loanAtStartOfTheMonth + crystallisedInterest - scheduledPrincipal
}

export const getAverageBalance = (
  loanAtStartOfTheMonth: number,
  loanAtEndOfMonth: number,
) => {
  return (loanAtStartOfTheMonth + loanAtEndOfMonth) / 2
}

export const displayOutputTable = (
  rows: { scheduledPrincipal: number; arrangementFee: number }[],
  fixRows: { scheduledPrincipal: number; arrangementFee: number }[],
  ltv: number,
  propertyType: string,
  years: number,
  baseRate: number,
  variableCommercial: number,
  variableResidential: number,
  fixedCommercial: number,
  fixedResidential: number,
) => {
  const clientOutputTable = [
    {
      columnName: 'Customer Interest Rate',
      variablePricing:
        propertyType === 'commercial'
          ? `${variableCommercial + baseRate}%`
          : `${variableResidential + baseRate}%`,
      fixPricing:
        propertyType === 'commercial'
          ? `${fixedCommercial}%`
          : `${fixedResidential}%`,
    },
    {
      columnName: `Regular repayments £ x ${years * 12}`,
      variablePricing: rows[1].scheduledPrincipal,
      fixPricing: fixRows[1].scheduledPrincipal,
    },
    {
      columnName: 'Final balloon payment at end of loan term',
      variablePricing: rows[rows.length - 1].scheduledPrincipal,
      fixPricing: fixRows[fixRows.length - 1].scheduledPrincipal,
    },
    {
      columnName: 'Product fee £',
      variablePricing: rows[0].arrangementFee,
      fixPricing: fixRows[0].arrangementFee,
    },
    {
      columnName: 'Loan to value',
      variablePricing: `${ltv.toFixed(2)}%`,
      fixPricing: `${ltv.toFixed(2)}%`,
    },
  ]
  return clientOutputTable
}

export const getMortgageCrystalisedInterest = (
  interestMonth: number,
  initialProductPeriod: number,
  initialProductRate: number,
  remainingMonths: number,
  mortgageAtStartOfTheMonth: number,
  baseRate: number,
  reversionMargin: number,
) => {
  if (interestMonth <= initialProductPeriod) {
    // return(mortgageAtStartOfTheMonth * (initialProductPeriod / 100)) / 12
    return Math.round(
      -finance.IPMT(
        initialProductRate / 100 / 12,
        1,
        remainingMonths,
        mortgageAtStartOfTheMonth,
      ),
    )
  } else {
    return Math.round(
      -finance.IPMT(
        (baseRate + reversionMargin) / 100 / 12,
        1,
        remainingMonths,
        mortgageAtStartOfTheMonth,
      ),
    )
  }
}

export const MortgageMonthlyPayments = (
  interestMonth: number,
  initialProductPeriod: number,
  initialProductRate: number,
  remainingMonths: number,
  mortgageAtStartOfTheMonth: number,
  baseRate: number,
  reversionMargin: number,
) => {
  if (interestMonth <= initialProductPeriod) {
    return -finance.PMT(
      initialProductRate / 100 / 12,
      remainingMonths,
      mortgageAtStartOfTheMonth,
    )
  } else {
    return -finance.PMT(
      (baseRate + reversionMargin) / 100 / 12,
      remainingMonths,
      mortgageAtStartOfTheMonth,
    )
  }
}

export const getMortgageAtTheEndOfTheMonth = (
  mortgageAtStartOfTheMonth: number,
  MortgageCrystalisedInterest: number,
  mortgageMonthlyPayments: number,
) => {
  return (
    mortgageAtStartOfTheMonth +
    MortgageCrystalisedInterest -
    mortgageMonthlyPayments
  )
}

export const CalculateMortgage = (
  vals:
    | {
        productFeeAdded: string
        borrowAmount: number
        yearsToPay: number
        monthsToPay: number
        product: string
      }
    | any,
  initialProduct:
    | {
        isProductFeePercantage: any
        productFee: number
        term: number
        rateToClient: number
        productName: any
      }
    | any,
) => {
  let amountWithFeesAdded = 0

  // const initialProduct = PRODUCTS_NEW_TO_BANK.tracker.find(
  //   (item) => item.value === vals.product
  // );

  if (
    vals.productFeeAdded === 'yes' &&
    initialProduct?.isProductFeePercantage
  ) {
    const amountToBeAdded =
      (initialProduct.productFee / 100) * vals.borrowAmount
    amountWithFeesAdded = Number(vals.borrowAmount) + amountToBeAdded
  } else if (
    vals.productFeeAdded === 'yes' &&
    !initialProduct?.isProductFeePercantage
  ) {
    amountWithFeesAdded = Number(vals.borrowAmount) + initialProduct?.productFee
  }
  // else if (
  //   vals.productFeeAdded === "no" &&
  //   initialProduct?.isProductFeePercantage
  // ) {
  //   let amountToBeAdded = (initialProduct.productFee / 100) * vals.borrowAmount;
  //   notAddedAmountWithFess = Number(vals.borrowAmount) + amountToBeAdded;
  // } else {
  //   notAddedAmountWithFess =
  //     Number(vals.borrowAmount) + initialProduct?.productFee;
  // }

  const baseRate = 3.5
  const reversionMargin = 3.89
  const overallPeriod = vals.yearsToPay * 12 + vals.monthsToPay

  const mortgagePaymentArray = [
    {
      mortgageAtTheStartOfTheMonth:
        amountWithFeesAdded === 0
          ? Number(vals.borrowAmount)
          : amountWithFeesAdded,
      mortgageCrystalisedInterest: 0,
      mortgageMonthlyPayments: 0,
      mortgageAtTheEndOfTheMonth:
        amountWithFeesAdded === 0
          ? Number(vals.borrowAmount)
          : amountWithFeesAdded,
    },
  ]

  for (let i = 1; i !== overallPeriod; i++) {
    const interestMonth = i

    const mortgageAtTheStartOfTheMonth =
      mortgagePaymentArray[i - 1].mortgageAtTheEndOfTheMonth

    const mortgageMonthlyPayments = MortgageMonthlyPayments(
      interestMonth,
      initialProduct?.term,
      initialProduct?.rateToClient,
      overallPeriod - i + 1,
      mortgageAtTheStartOfTheMonth,
      baseRate,
      reversionMargin,
    )

    const mortgageCrystalisedInterest = getMortgageCrystalisedInterest(
      interestMonth,
      initialProduct?.term,
      initialProduct?.rateToClient,
      overallPeriod - i + 1,
      mortgageAtTheStartOfTheMonth,
      baseRate,
      reversionMargin,
    )

    // if (i === 1 || i === 2) {
    //   console.log(i, mortgageCrystalisedInterest, mortgageMonthlyPayments);
    // }

    const mortgageAtTheEndOfTheMonth = getMortgageAtTheEndOfTheMonth(
      mortgageAtTheStartOfTheMonth,
      mortgageCrystalisedInterest,
      mortgageMonthlyPayments,
    )

    const newEntry = {
      mortgageAtTheStartOfTheMonth: mortgageAtTheStartOfTheMonth,
      mortgageCrystalisedInterest: mortgageCrystalisedInterest,
      mortgageMonthlyPayments: mortgageMonthlyPayments,
      mortgageAtTheEndOfTheMonth: mortgageAtTheEndOfTheMonth,
    }
    mortgagePaymentArray.push(newEntry)
  }

  // console.log("this is sum", sum[sum.length - 1]);

  // const aprc = (1+ (vals.borrowAmount - ))

  const finalisedResults = {
    productName: initialProduct.productName,
    initialMonthlyPayments: mortgagePaymentArray[1].mortgageMonthlyPayments,
    productFee: initialProduct?.isProductFeePercantage
      ? (initialProduct.productFee / 100) * vals.borrowAmount
      : initialProduct?.productFee,
    initialProductRate: initialProduct?.rateToClient,
    aprc: 0,
    followUpRate: baseRate + reversionMargin,
    followUpPayments: mortgagePaymentArray[25].mortgageMonthlyPayments,
  }
  return finalisedResults
}

/**
 * @deprecated use chakra cx instead
 */
export const classNames = (classes: Array<any>) => {
  return classes.filter(Boolean).join(' ')
}

/**
 * Typesafe capitalisation of first letter.
 */
export const capitalise = <T extends string>(string: T) =>
  (string[0].toUpperCase() + string.slice(1)) as Capitalize<typeof string>
